import { useEffect, useState } from "react";

const pad = (n) => String(n).padStart(2, "0");

function formatDate(date) {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

const CACHE_TTL = 60 * 60 * 1000; // 1 hour

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

/**
 * Fetches the next 3 days of high/low tide predictions from the NOAA CO-OPS
 * free public API (no API key required) for the given station.
 *
 * Returns { predictions, loading, error }  where predictions is an array of:
 *   { type: "H"|"L", time: Date, heightFt: number }
 */
export default function useTidePredictions({ stationId, enabled = true, isOffline = false } = {}) {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !stationId) {
      setPredictions([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetch3Days = async () => {
      setLoading(true);
      setError(null);

      const cacheKey = `the99:tidepreds:${stationId}:${formatDate(new Date())}`;
      const cached = readCache(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
        if (!cancelled) {
          setPredictions(cached.predictions);
          setLoading(false);
        }
        return;
      }

      // If offline, try any cached day for this station as a fallback
      if (isOffline || !navigator.onLine) {
        const offlineFallback = readCache(cacheKey);
        if (offlineFallback) {
          if (!cancelled) { setPredictions(offlineFallback.predictions); setLoading(false); }
        } else {
          // Try previous cached days
          const yesterday = new Date(Date.now() - 86400000);
          const prevKey = `the99:tidepreds:${stationId}:${formatDate(yesterday)}`;
          const prevCached = readCache(prevKey);
          if (!cancelled) {
            setPredictions(prevCached ? prevCached.predictions : []);
            setError(prevCached ? null : "Offline — no cached tide data");
            setLoading(false);
          }
        }
        return;
      }

      try {
        const now = new Date();
        const begin = formatDate(now);
        // 3 full days ahead
        const endDate = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
        const end = formatDate(endDate);

        const url =
          `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter` +
          `?product=predictions&application=the99` +
          `&begin_date=${begin}&end_date=${end}` +
          `&datum=MLLW&station=${stationId}` +
          `&units=english&time_zone=lst_ldt` +
          `&interval=hilo&format=json`;

        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`NOAA API ${res.status}`);

        const json = await res.json();
        const raw = json?.predictions;
        if (!Array.isArray(raw) || raw.length === 0) {
          throw new Error("No tide prediction data returned");
        }

        const parsed = raw.map((p) => ({
          type: p.type === "H" ? "H" : "L",
          time: new Date(p.t),
          heightFt: parseFloat(p.v),
        }));

        writeCache(cacheKey, { fetchedAt: Date.now(), predictions: parsed.map((p) => ({ ...p, time: p.time.toISOString() })) });

        if (!cancelled) {
          setPredictions(parsed);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.name === "AbortError" ? "Request timed out" : (err.message || "Failed to load tides"));
          setPredictions([]);
          setLoading(false);
        }
      }
    };

    fetch3Days();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [stationId, enabled, isOffline]);

  // Rehydrate cached predictions from ISO strings back to Date objects
  const hydratedPredictions = predictions.map((p) => ({
    ...p,
    time: p.time instanceof Date ? p.time : new Date(p.time),
  }));

  return { predictions: hydratedPredictions, loading, error };
}
